/**
 * Notification template merge markers (`task.md`):
 * - **Unclosed** `{{` without `}}` before the next `{{` or end of string.
 * - **Empty** pair `{{}}` / `{{ }}` (inner whitespace-only) — `empty_variable`.
 * - **Markup** in a closed pair: inner contains `<` or `>` — `markup_in_merge_field` (`task.md` HTML case).
 * Other `{{…}}` with non-empty inner text is allowed. Single-brace `{…}` in prose is ignored.
 *
 * {@link isValidMergeInner} / {@link isValidHandleBars} remain for other callers (TinyMCE / email-builder parity);
 * this module’s {@link validateTemplateVariables} uses the brace rule above only.
 */

/** Same invalid-pattern regex as TinyMCE + email-builder `isValidHandleBars`. */
export const HANDLEBARS_INVALID_REGEX =
  /((^{{|[^{\n\r]{{)(?!{))(?!( ?(\w\.?)+ ?(}}(?!}))))|((^{{{|[^{\n\r]{{{)(?!{))(?!( ?(\w\.?)+ ?(}}}(?!}))))|{{4,}|^{(?= )/m

/**
 * Opening/closing block helpers — when present, `isValidHandleBars` skips the strict regex
 * (`ghl-email-builder/apps/common/utils/helper.ts` lines 135–141).
 */
export const BLOCK_HELPER_PATTERN =
  /\{\{#([\w.\-\/]+)(?:\s+[\s\S]*?)?\}\}|\{\{\/([\w.\-\/]+)\}\}/

/** See `task.md` — unclosed `{{`, empty pair, or angle brackets inside a closed pair. */
export type TemplateVariableIssue = 'unclosed_open' | 'empty_variable' | 'markup_in_merge_field'

export interface TemplateVariableError {
  snippet: string
  issue: TemplateVariableIssue
  start: number
  end: number
}

export interface ValidateTemplateVariablesResult {
  valid: boolean
  errors: TemplateVariableError[]
}

/**
 * Dotted merge path: segments are unquoted `[a-zA-Z_][\w#-]*` or single-quoted `'…'`.
 * `#` appears in API/custom keys (e.g. `inv#`); block opens are only `{{#…}}` (inner starts with `#`).
 * Examples: `contact.email`, `contact.first-name`, `inv#`, `contact.'first-name'`
 */
const INNER_MERGE_PATH =
  /^(?:[a-zA-Z_][\w#-]*|'[^']*')(?:\.(?:[a-zA-Z_][\w#-]*|'[^']*'))*$/

/**
 * Helper-style merge content: two+ space-separated identifier tokens.
 * Example: `rss_img image_url`
 */
const INNER_MERGE_HELPER = /^[\w][\w#.-]*(?:\s+[\w][\w#.-]*)+$/

/**
 * True if inner text between `{{` and `}}` is a supported merge field shape
 * (dotted paths with hyphens / quoted segments, or space-separated helper-style tokens).
 */
export function isValidMergeInner(inner: string): boolean {
  return INNER_MERGE_PATH.test(inner) || INNER_MERGE_HELPER.test(inner)
}

/** True if the string contains `{` or `}` (after `ghl-email-builder` `hasHandleBar`). */
export function hasHandleBar(value: string | undefined | null): boolean {
  const s = value ?? ''
  const replacedBars = s.replace(/\{/g, '').replace(/\}/g, '')
  return replacedBars !== s
}

/**
 * Mirrors `ghl-email-builder` `isValidHandleBars`: block helpers → valid; else regex must not match.
 */
export function isValidHandleBars(value: string): boolean {
  if (BLOCK_HELPER_PATTERN.test(value)) {
    return true
  }
  return !HANDLEBARS_INVALID_REGEX.test(value)
}

/** Same as `isValidHandleBars`; name matches `spm-ts` VueTinyMCE `handlebarsValidator`. */
export function handlebarsValidator(value: string): boolean {
  return isValidHandleBars(value)
}

/**
 * Invalid when this `{{` has no `}}` before the **next** `{{` or before end of string.
 *
 * @param content - Plain text or HTML (`isHtml` kept for API compatibility).
 */
export function validateTemplateVariables(
  content: string,
  _options?: { isHtml?: boolean }
): ValidateTemplateVariablesResult {
  const raw = content ?? ''
  const errors: TemplateVariableError[] = []

  let pos = 0
  while (pos < raw.length) {
    const open = raw.indexOf('{{', pos)
    if (open === -1) {
      break
    }
    const nextOpen = raw.indexOf('{{', open + 2)
    const close = raw.indexOf('}}', open + 2)
    const regionEnd = nextOpen === -1 ? raw.length : nextOpen
    const hasCloseInRegion = close !== -1 && close < regionEnd
    if (!hasCloseInRegion) {
      errors.push({
        snippet: raw.slice(open, Math.min(raw.length, open + 40)),
        issue: 'unclosed_open',
        start: open,
        end: raw.length,
      })
      pos = nextOpen === -1 ? raw.length : nextOpen
      continue
    }
    const pairEnd = close + 2
    const inner = raw.slice(open + 2, close).trim()
    if (inner.length === 0) {
      errors.push({
        snippet: raw.slice(open, pairEnd),
        issue: 'empty_variable',
        start: open,
        end: pairEnd,
      })
    } else if (inner.includes('<') || inner.includes('>')) {
      errors.push({
        snippet: raw.slice(open, pairEnd),
        issue: 'markup_in_merge_field',
        start: open,
        end: pairEnd,
      })
    }
    pos = pairEnd
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

const DEFAULT_SNIPPET_MAX = 200

function stripHtmlTagsForDisplay(s: string): string {
  return s.replace(/<[^>]*>/g, ' ')
}

/** Merge-field and tooltip copy must never show raw HTML from rich-text / email bodies. */
function sanitizeMergeSnippetForUi(s: string): string {
  return stripHtmlTagsForDisplay(s).replace(/\s+/g, ' ').trim()
}

/**
 * When the user types `{{ a } b {{` (one `}` too few before the next `{{`), the pair scanner
 * associates the first `{{` with the *last* `}}` in the string, so `pairSnippet` is huge and
 * `getTemplateVariableIssueSnippets` would show the whole subject. We only surface the first
 * intended merge for the warning (e.g. `{{ custom_values.services_1 }}`).
 */
function extractFirstBrokenChainedMergeDisplay(cleaned: string): string | null {
  const startOpen = cleaned.indexOf('{{')
  if (startOpen === -1) {
    return null
  }
  const rest = cleaned.slice(startOpen + 2)
  const splitMatch = rest.match(/^([\s\S]*?)\}\s*\{\{/)
  if (!splitMatch) {
    return null
  }
  const inner = splitMatch[1]!.replace(/\s+/g, ' ').trim()
  if (!inner || inner.includes('{{') || inner.includes('}}')) {
    return null
  }
  return `{{ ${inner} }}`
}

/**
 * Pulls `{{field}}` / malformed `{{field}` tokens from a raw error slice so warnings never show HTML
 * or mispaired-scan junk (e.g. `{{appointment.title}<br> Date...`).
 */
function extractMergeFieldTokensForDisplay(text: string): string[] {
  const cleaned = stripHtmlTagsForDisplay(text).replace(/\s+/g, ' ').trim()
  const seen = new Set<string>()
  const empty: string[] = []
  if (/\{\{\s*\}\}/.test(cleaned)) {
    empty.push('{{}}')
    seen.add('{{}}')
  }

  const brokenChained = extractFirstBrokenChainedMergeDisplay(cleaned)
  if (brokenChained) {
    return [...empty, brokenChained]
  }

  const reComplete = /\{\{[a-zA-Z_][a-zA-Z0-9_.]*\}\}/g
  const completes: string[] = []
  let m: RegExpExecArray | null
  while ((m = reComplete.exec(cleaned)) !== null) {
    if (!seen.has(m[0])) {
      seen.add(m[0])
      completes.push(m[0])
    }
  }

  const rePartial = /\{\{[a-zA-Z_][a-zA-Z0-9_.]*\}(?!\})/g
  const partials: string[] = []
  while ((m = rePartial.exec(cleaned)) !== null) {
    if (!seen.has(m[0])) {
      seen.add(m[0])
      partials.push(m[0])
    }
  }

  // If any `{{field}` is missing a closing `}`, the pair scanner can mis-associate later
  // `{{...}}` tokens; listing those complete tokens blames the wrong merge fields in UI.
  if (partials.length > 0) {
    return [...empty, ...partials]
  }
  return [...empty, ...completes]
}

function fallbackSnippetWithoutHtml(snippet: string, maxLen: number): string | null {
  const noHtml = stripHtmlTagsForDisplay(snippet).replace(/\s+/g, ' ').trim()
  if (!noHtml) {
    return null
  }
  if (noHtml.length > maxLen) {
    return `${noHtml.slice(0, maxLen)}…`
  }
  return noHtml
}

const MAX_UNCLOSED_MERGE_DISPLAY = 80

/**
 * Tooltip line for an `unclosed_open` error: only the broken merge token (e.g. `{{contact.name}`),
 * not the rest of the sentence on the same line.
 */
function extractUnclosedMergeDisplayToken(source: string, start: number, maxLen: number): string {
  if (start < 0 || start + 2 > source.length || source.slice(start, start + 2) !== '{{') {
    return ''
  }
  const n = source.length
  let i = start + 2
  while (i < n && (source[i] === ' ' || source[i] === '\t')) {
    i++
  }
  if (i >= n) {
    const t = source.slice(start, Math.min(n, start + maxLen))
    return sanitizeMergeSnippetForUi(t.length > maxLen ? `${t.slice(0, Math.max(0, maxLen - 1))}…` : t)
  }

  const c0 = source[i]
  if (c0 === '#' || c0 === '/' || c0 === '!' || c0 === '>') {
    let j = start
    const limit = Math.min(n, start + Math.max(maxLen, MAX_UNCLOSED_MERGE_DISPLAY))
    while (j < limit) {
      const ch = source[j]
      if (ch === '\n' || ch === '\r') {
        return sanitizeMergeSnippetForUi(source.slice(start, j))
      }
      if (ch === '}' && j + 1 < n && source[j + 1] === '}') {
        return sanitizeMergeSnippetForUi(source.slice(start, j + 2))
      }
      if (ch === '}' && (j + 1 >= n || source[j + 1] !== '}')) {
        return sanitizeMergeSnippetForUi(source.slice(start, j + 1))
      }
      j++
    }
    return sanitizeMergeSnippetForUi(source.slice(start, limit))
  }

  i = start + 2
  while (i < n && (source[i] === ' ' || source[i] === '\t')) {
    i++
  }
  const pathStart = i
  while (i < n) {
    const ch = source[i]
    if (ch === '\n' || ch === '\r') {
      break
    }
    if (ch === '}') {
      if (i + 1 < n && source[i + 1] === '}') {
        return sanitizeMergeSnippetForUi(source.slice(start, i + 2))
      }
      return sanitizeMergeSnippetForUi(source.slice(start, i + 1))
    }
    if (ch === "'") {
      i++
      while (i < n && source[i] !== "'") {
        i++
      }
      if (i < n) {
        i++
      }
      continue
    }
    if (/[\w#.-]/.test(ch)) {
      while (i < n && /[\w#.-]/.test(source[i])) {
        i++
      }
      continue
    }
    if (ch === '.' && i + 1 < n && /[\w#]/.test(source[i + 1]!)) {
      i++
      continue
    }
    if ((ch === ' ' || ch === '\t') && i > pathStart) {
      let j = i
      while (j < n && (source[j] === ' ' || source[j] === '\t')) {
        j++
      }
      if (j < n && source[j] === '}') {
        i = j
        continue
      }
      break
    }
    break
  }
  let token = source.slice(start, i)
  if (token.length > maxLen) {
    token = `${token.slice(0, Math.max(0, maxLen - 1))}…`
  }
  return sanitizeMergeSnippetForUi(token)
}

function filterHoverListNoise(snippets: string[]): string[] {
  return snippets
}

function displaysForErrorHover(source: string, e: TemplateVariableError, maxLen: number): string[] {
  if (e.issue === 'empty_variable') {
    const compact = sanitizeMergeSnippetForUi((e.snippet ?? '').trim())
    return [(compact.length > 0 ? compact : '{{ }}').slice(0, maxLen)].filter(s => s.length > 0)
  }
  if (e.issue === 'markup_in_merge_field') {
    const compact = sanitizeMergeSnippetForUi((e.snippet ?? '').trim())
    return [(compact.length > 0 ? compact : '{{}}').slice(0, maxLen)].filter(s => s.length > 0)
  }
  if (e.issue !== 'unclosed_open') {
    return []
  }
  if (e.start < 0 || e.start > source.length) {
    return []
  }
  return [extractUnclosedMergeDisplayToken(source, e.start, maxLen)].filter(s => s.length > 0)
}

/**
 * Unique problematic merge-field snippets for display (order preserved).
 * Strips HTML and prefers extracted `{{...}}` tokens so warnings list only merge fields, not markup.
 * Pass `sourceText` (original field) so the default hover/title list shows the right problem sites.
 */
export function getTemplateVariableIssueSnippets(
  result: ValidateTemplateVariablesResult,
  options?: { maxSnippetLength?: number; sourceText?: string }
): string[] {
  if (result.valid || !result.errors.length) {
    return []
  }
  const maxLen = options?.maxSnippetLength ?? DEFAULT_SNIPPET_MAX
  const sourceText = options?.sourceText
  const seen = new Set<string>()
  const out: string[] = []
  if (sourceText) {
    for (const e of result.errors) {
      const posKey = `s:${e.start}`
      if (seen.has(posKey)) {
        continue
      }
      for (const d of displaysForErrorHover(sourceText, e, maxLen)) {
        if (d.length > 0) {
          seen.add(posKey)
          out.push(d)
        }
      }
    }
    const combined = filterHoverListNoise(out)
    if (combined.length > 0) {
      // One entry per error `start`; do not collapse duplicate display text (e.g. two `{{contact.name}`).
      const cleaned = combined.map(sanitizeMergeSnippetForUi).filter(s => s.length > 0)
      if (cleaned.length > 0) {
        return cleaned
      }
    }
  }
  out.length = 0
  seen.clear()
  for (const e of result.errors) {
    const raw = (e.snippet ?? '').trim()
    if (!raw) {
      continue
    }
    const tokens = extractMergeFieldTokensForDisplay(raw)
    if (tokens.length > 0) {
      for (const t of tokens) {
        if (!seen.has(t)) {
          seen.add(t)
          out.push(t)
        }
      }
      continue
    }
    const fb = fallbackSnippetWithoutHtml(raw, maxLen)
    if (fb && !seen.has(fb)) {
      seen.add(fb)
      out.push(fb)
    }
  }
  return filterHoverListNoise(dedupeKeepOrder(out.map(sanitizeMergeSnippetForUi).filter(s => s.length > 0)))
}

function dedupeKeepOrder(items: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const x of items) {
    if (seen.has(x)) {
      continue
    }
    seen.add(x)
    out.push(x)
  }
  return out
}

export interface TemplateVariableFieldIssue {
  fieldLabel: string
  snippets: string[]
}

/** Per-field validation result for building user-facing messages. */
export interface TemplateVariableFieldValidation {
  fieldLabel: string
  result: ValidateTemplateVariablesResult
  /** Original field text — enables position-accurate hover/title lists for multi-error templates. */
  sourceText: string
}

/**
 * All fields that fail {@link validateTemplateVariables} (including those with no display snippets).
 */
export function collectInvalidTemplateVariableFields(
  fields: Array<{ text: string; isHtml?: boolean; fieldLabel: string }>
): TemplateVariableFieldValidation[] {
  const out: TemplateVariableFieldValidation[] = []
  for (const f of fields) {
    const r = validateTemplateVariables(f.text, { isHtml: f.isHtml })
    if (!r.valid) {
      out.push({ fieldLabel: f.fieldLabel, result: r, sourceText: f.text })
    }
  }
  return out
}

export type TemplateVariableWarningKind = 'markup' | 'empty' | 'single' | 'default'

/**
 * UI warning kind (`task.md`): `markup` if any `markup_in_merge_field`; else `empty` if any
 * `empty_variable`; else `single` for one snippet; else `default`.
 */
export function resolveTemplateVariableWarningKind(
  fieldValidations: TemplateVariableFieldValidation[]
): TemplateVariableWarningKind {
  const allErrors = fieldValidations.flatMap(fv => fv.result.errors)
  if (allErrors.some(e => e.issue === 'markup_in_merge_field')) {
    return 'markup'
  }
  if (allErrors.some(e => e.issue === 'empty_variable')) {
    return 'empty'
  }
  const snippets = getDedupedTemplateVariableSnippets(fieldValidations)
  if (snippets.length === 1) {
    return 'single'
  }
  return 'default'
}

export function getDedupedTemplateVariableSnippets(
  fieldValidations: TemplateVariableFieldValidation[]
): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const fv of fieldValidations) {
    for (const snip of getTemplateVariableIssueSnippets(fv.result, {
      sourceText: fv.sourceText,
    })) {
      if (!seen.has(snip)) {
        seen.add(snip)
        out.push(snip)
      }
    }
  }
  return out
}

/**
 * Validates each field separately so the UI can show **where** bad merge fields are
 * (e.g. subject vs. body). Pass translated `fieldLabel` strings from i18n.
 */
export function collectTemplateVariableFieldIssues(
  fields: Array<{ text: string; isHtml?: boolean; fieldLabel: string }>
): TemplateVariableFieldIssue[] {
  const out: TemplateVariableFieldIssue[] = []
  for (const fv of collectInvalidTemplateVariableFields(fields)) {
    const snippets = getTemplateVariableIssueSnippets(fv.result, {
      sourceText: fv.sourceText,
    })
    if (snippets.length) {
      out.push({ fieldLabel: fv.fieldLabel, snippets })
    }
  }
  return out
}

/** Skip validation work when there are no merge-field markers. */
export function mightContainMergeFields(content: string | undefined | null): boolean {
  return typeof content === 'string' && content.includes('{{')
}
